/**
 * Filter tree + the PostgREST `.or()` mini-language parser.
 *
 * Every `.eq()/.in()/.not()/…` call appends one node to a top-level AND list.
 * `.or('a.eq.1,and(b.eq.2,c.is.null)')` parses into a nested tree.
 */
import type { Row } from './types';
import {
  arrayContains, arrayOverlaps, compareValues, isMatch, isNullish, likeMatch, looseEq,
} from './values';

export type CmpOp =
  | 'eq' | 'neq' | 'in' | 'is'
  | 'gt' | 'gte' | 'lt' | 'lte'
  | 'like' | 'ilike'
  | 'cs' | 'ov';

export type FilterNode =
  | { t: 'cmp'; col: string; op: CmpOp; value: unknown; negate: boolean }
  | { t: 'and'; nodes: FilterNode[] }
  | { t: 'or'; nodes: FilterNode[] };

export function cmp(col: string, op: CmpOp, value: unknown, negate = false): FilterNode {
  return { t: 'cmp', col, op, value, negate };
}

/** Reads `col` off a row; embeds are never filtered on, so this stays flat. */
function cellOf(row: Row, col: string): unknown {
  return row[col];
}

function cmpMatch(row: Row, node: Extract<FilterNode, { t: 'cmp' }>): boolean {
  const v = cellOf(row, node.col);
  const raw = node.value;

  if (node.op === 'is') {
    // `IS NULL/TRUE/FALSE` is three-valued-safe: negation is a plain `!`.
    return node.negate ? !isMatch(v, raw) : isMatch(v, raw);
  }

  let hit: boolean;
  switch (node.op) {
    case 'eq': hit = !isNullish(v) && looseEq(v, raw); break;
    case 'neq': hit = !isNullish(v) && !looseEq(v, raw); break;
    case 'in': {
      const list = Array.isArray(raw) ? raw : [raw];
      hit = !isNullish(v) && list.some((x) => looseEq(v, x));
      break;
    }
    case 'gt': hit = !isNullish(v) && !isNullish(raw) && compareValues(v, raw) > 0; break;
    case 'gte': hit = !isNullish(v) && !isNullish(raw) && compareValues(v, raw) >= 0; break;
    case 'lt': hit = !isNullish(v) && !isNullish(raw) && compareValues(v, raw) < 0; break;
    case 'lte': hit = !isNullish(v) && !isNullish(raw) && compareValues(v, raw) <= 0; break;
    case 'like': hit = likeMatch(v, raw, false); break;
    case 'ilike': hit = likeMatch(v, raw, true); break;
    case 'cs': hit = arrayContains(v, raw); break;
    case 'ov': hit = arrayOverlaps(v, raw); break;
    default: hit = false;
  }

  if (!node.negate) return hit;
  // SQL: NOT(NULL <op> x) is NULL, i.e. the row does NOT pass. `.not(col,'eq',v)`
  // therefore also drops rows where `col` is NULL — matching PostgREST.
  return !isNullish(v) && !hit;
}

export function matchNode(row: Row, node: FilterNode): boolean {
  switch (node.t) {
    case 'cmp': return cmpMatch(row, node);
    case 'and': return node.nodes.every((n) => matchNode(row, n));
    case 'or': return node.nodes.some((n) => matchNode(row, n));
  }
}

export function matchAll(row: Row, nodes: FilterNode[]): boolean {
  for (const n of nodes) if (!matchNode(row, n)) return false;
  return true;
}

// ── `.or()` parsing ─────────────────────────────────────────────────────────

/**
 * Split on commas that sit at paren depth 0 and outside double quotes, so
 * `team_id.in.(1,2)` and `and(a.eq.1,b.eq.2)` survive as single terms.
 */
export function splitTopLevel(input: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let quoted = false;
  let cur = '';
  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i];
    if (quoted) {
      cur += ch;
      if (ch === '\\' && i + 1 < input.length) { cur += input[i + 1]; i += 1; }
      else if (ch === '"') quoted = false;
      continue;
    }
    if (ch === '"') { quoted = true; cur += ch; continue; }
    if (ch === '(') { depth += 1; cur += ch; continue; }
    if (ch === ')') { depth -= 1; cur += ch; continue; }
    if (ch === ',' && depth === 0) { out.push(cur); cur = ''; continue; }
    cur += ch;
  }
  out.push(cur);
  return out.map((s) => s.trim()).filter((s) => s.length > 0);
}

function unquote(raw: string): string {
  const s = raw.trim();
  if (s.length >= 2 && s.startsWith('"') && s.endsWith('"')) {
    return s.slice(1, -1).replace(/\\"/g, '"');
  }
  return s;
}

const OPS = new Set<string>([
  'eq', 'neq', 'in', 'is', 'gt', 'gte', 'lt', 'lte', 'like', 'ilike', 'cs', 'ov',
]);

function parseValue(op: CmpOp, raw: string): unknown {
  if (op === 'in') {
    const trimmed = raw.trim();
    const inner = trimmed.startsWith('(') && trimmed.endsWith(')')
      ? trimmed.slice(1, -1)
      : trimmed;
    return splitTopLevel(inner).map(unquote);
  }
  if (op === 'is') {
    const s = raw.trim().toLowerCase();
    if (s === 'null' || s === 'unknown') return null;
    if (s === 'true') return true;
    if (s === 'false') return false;
    return unquote(raw);
  }
  return unquote(raw);
}

/** Parses one `col.op.value` term, or a nested `and(...)` / `or(...)` group. */
export function parseTerm(input: string): FilterNode | null {
  const s = input.trim();
  const group = /^(and|or|not\.and|not\.or)\s*\(([\s\S]*)\)$/i.exec(s);
  if (group) {
    const kind = group[1]!.toLowerCase();
    const nodes = splitTopLevel(group[2]!)
      .map(parseTerm)
      .filter((n): n is FilterNode => n !== null);
    if (nodes.length === 0) return null;
    // `not.and(...)` is rare and unused here; treat it as its positive form
    // rather than throwing — a demo should never blank a screen over syntax.
    return kind.endsWith('or') ? { t: 'or', nodes } : { t: 'and', nodes };
  }

  const firstDot = s.indexOf('.');
  if (firstDot < 0) return null;
  const col = s.slice(0, firstDot);
  let rest = s.slice(firstDot + 1);

  let negate = false;
  if (rest.toLowerCase().startsWith('not.')) {
    negate = true;
    rest = rest.slice(4);
  }

  const opDot = rest.indexOf('.');
  if (opDot < 0) return null;
  const opRaw = rest.slice(0, opDot).toLowerCase();
  const valueRaw = rest.slice(opDot + 1);
  if (!OPS.has(opRaw)) return null;
  const op = opRaw as CmpOp;
  return cmp(col, op, parseValue(op, valueRaw), negate);
}

/** `.or(filterString)` → one OR node. Unparseable terms are dropped. */
export function parseOrFilter(input: string): FilterNode | null {
  const nodes = splitTopLevel(input)
    .map(parseTerm)
    .filter((n): n is FilterNode => n !== null);
  if (nodes.length === 0) return null;
  return { t: 'or', nodes };
}
